import { Mutation } from '@apollo/client/react/components';
import type { MutationFunction as MutationFn } from '@apollo/client/react/types/types';
import * as React from 'react';
import Text from 'components/Text';
import Row from 'components/Row';
import Heading from 'components/Heading';

import Radio from 'components/Radio';
import SET_PARTICIPANT_MESSAGING_MUTATION from './SET_PARTICIPANT_MESSAGING_MUTATION.gql';
import ToastActions from 'actions/ToastActions';
import { ApolloError } from '@apollo/client';
import {
  setParticipantMessaging,
  setParticipantMessagingVariables,
} from './__generated__/setParticipantMessaging';

export type SetParticipantMessagingMutationFn = MutationFn<
  setParticipantMessaging,
  setParticipantMessagingVariables
>;

type Props = {
  participantMessagingSchoolDefault: string;
  participantMessaging: string;
  participantMessagingSelect: (
    setParticipantMessaging: SetParticipantMessagingMutationFn,
    option: string
  ) => void;
  disabled: boolean;
};
const ParticipantMessagingSelect = ({
  participantMessaging,
  participantMessagingSchoolDefault,
  participantMessagingSelect,
  disabled,
}: Props) => (
  <Mutation<setParticipantMessaging, setParticipantMessagingVariables>
    mutation={SET_PARTICIPANT_MESSAGING_MUTATION}
    onCompleted={() => ToastActions.showSuccessToast('Saved')}
    onError={(error: ApolloError) => ToastActions.showErrorToast(error.message)}
  >
    {(setParticipantMessagingMutation) => (
      <Row>
        <Heading data-test="GroupOptionsCheckbox-title" level="3" levelStyle="5" align="left">
          Participant messaging
        </Heading>
        <Text
          data-test="GroupOptionsCheckbox-sublabel"
          element="div"
          color="graphite"
          size="xsmall"
        >
          Choose how participants see and message others in the class.
        </Text>
        {participantMessagingSchoolDefault && (
          <Text
            data-test="GroupOptionsCheckbox-schoolDefault"
            element="div"
            color="graphite"
            size="xsmall"
            italic
            lineHeight="xlarge"
          >
            School default: {participantMessagingSchoolDefault}
          </Text>
        )}
        <Row>
          <Radio
            disabled={disabled}
            data-test="GroupOptionsRadioSelect-option"
            label="On"
            labelNote="All participants in the class can message each other."
            checked={participantMessaging === 'open'}
            onChange={() => {
              participantMessagingSelect(setParticipantMessagingMutation, 'open');
            }}
          />
          <Radio
            disabled={disabled}
            data-test="GroupOptionsRadioSelect-option"
            label="Role-based"
            labelNote="Students can only message students; parents can only message parents."
            checked={participantMessaging === 'ownerOrSameRole'}
            onChange={() => {
              participantMessagingSelect(setParticipantMessagingMutation, 'ownerOrSameRole');
            }}
          />
          <Radio
            disabled={disabled}
            data-test="GroupOptionsRadioSelect-option"
            label="Off"
            labelNote="Participants can only message class owners and can’t see others in the class."
            checked={participantMessaging === 'ownersOnly'}
            onChange={() => {
              participantMessagingSelect(setParticipantMessagingMutation, 'ownersOnly');
            }}
          />
        </Row>
      </Row>
    )}
  </Mutation>
);

export default ParticipantMessagingSelect;
